import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import { DateRange, PagedList, StorageInvoiceModel, StorageInvoiceProductModel } from '../interface';
import { toHttpParams } from '../utils/globals';
import { StorageInvoiceStatus } from '../enum';
import { PaymentType, StorageInvoiceResourceService } from '../vn-api';

interface StorageInvoiceListParam {
  page?: number;
  limit?: number;
  query?: string;
  createDate?: DateRange;
  incomingDate?: DateRange;
  paidDate?: DateRange;
  productInfoIds?: number[];
  supplierId?: number;
  spendCategoryId?: number;
  paid?: boolean;
  statuses?: StorageInvoiceStatus[];
  storeIds?: number[];
  dueDate?: DateRange;
  paymentApprovalId?: number;
}

export interface StorageInvoicePaymentRequest {
  invoiceIds: number[];
  paymentTypes: {organizationId: number, paymentType: PaymentType}[];
}

@Injectable({
  providedIn: 'root'
})
export class HxStorageInvoiceService {
  constructor(
    private http: HttpClient,
    private storageInvoiceResource: StorageInvoiceResourceService,
  ) {
  }

  getStorageInvoiceList(params?: StorageInvoiceListParam): Observable<PagedList<StorageInvoiceModel>> {
    // note: spendCategoryId is not a query param of GET /storage-invoices (backend ignores it), so it is not forwarded
    return this.storageInvoiceResource.getStorageInvoices(
      params?.createDate?.from,
      params?.createDate?.to,
      params?.dueDate?.from,
      params?.dueDate?.to,
      params?.incomingDate?.from,
      params?.incomingDate?.to,
      params?.limit,
      params?.page,
      params?.paid,
      params?.paidDate?.from,
      params?.paidDate?.to,
      params?.paymentApprovalId,
      params?.productInfoIds,
      params?.query,
      params?.statuses as any,
      params?.storeIds,
      params?.supplierId,
    ) as unknown as Observable<PagedList<StorageInvoiceModel>>;
  }

  getStorageInvoiceById(invoiceId: number, params?: {paymentApprovalId?: number}): Observable<StorageInvoiceModel> {
    return this.http.get<StorageInvoiceModel>(`/api/vn/storage-invoices/${invoiceId}`, {params: toHttpParams(params, true)});
  }

  saveInvoice(request: StorageInvoiceRequest): Promise<{id: number}> {
    return firstValueFrom(this.http.post<{id: number}>(`/api/vn/storage-invoices`, request));
  }

  receiveInvoice(invoiceId: number, request: StorageInvoiceRequest): Observable<void> {
    return this.http.post<void>(`/api/vn/storage-invoices/${invoiceId}/received`, request);
  }

  rejectInvoice(invoiceId: number, reason: string): Observable<void> {
    return this.http.post<void>(`/api/vn/storage-invoices/${invoiceId}/rejected`, {reason: reason});
  }

  cancelInvoice(invoiceId: number, reason: string): Observable<void> {
    return this.http.post<void>(`/api/vn/storage-invoices/${invoiceId}/cancelled`, {reason: reason});
  }

  paidInvoice(request: StorageInvoicePaymentRequest): Observable<void> {
    return this.http.post<void>(`/api/vn/storage-invoices/paid`, request);
  }
}

export interface StorageInvoiceRequest {
  id?: number;
  incomingInvoiceNumber: string;
  incomingInvoiceDate: string;
  storeId: number;
  paymentType: PaymentType;
  supplierId: number;
  products: StorageInvoiceProductModel[];
  fileIds: number[];
  spendType: string;
  spendCategoryCode: string;
}
